import { generateDownloadUrl, FileDownloader, generateOriginalWatermarkedImage, getImageInfo, identifyResourceType } from "@xmiles/holopix-helper";
import { notification } from "ant-design-vue";
import { h } from "vue";
import { useUserStore } from "@/store/user";
import { WatermarkStatusEnum } from "@/types/alipayRealName";
import { nanoid } from "nanoid";
import { addBlindWatermark } from 'holopix-api';
import { getFileExtension } from ".";
import useGroupStore from "@/store/group";
import { logger } from '@/utils/logger';

/**
 * 阿里云盲水印支持的图片格式
 */
const ALIYUN_SUPPORTED_FORMATS = ['jpg', 'jpeg', 'png', 'bmp', 'webp', 'tiff'];

/**
 * 检查图片是否符合阿里云盲水印服务要求
 */
export function validateImageForAliyunBlindWatermark(imageUrl: string): {
  isValid: boolean;
  reason?: string;
} {
  if (!imageUrl.includes('https://genai.holopix.cn')) {
    return {
      isValid: false,
      reason: '不是阿里云存储域名'
    };
  }
  const fileExtension = getFileExtension(imageUrl)?.toLowerCase();
  
  // 检查格式支持
  if (!fileExtension || !ALIYUN_SUPPORTED_FORMATS.includes(fileExtension)) {
    return {
      isValid: false,
      reason: `不支持的图片格式: ${fileExtension || '未知'}，阿里云盲水印仅支持 ${ALIYUN_SUPPORTED_FORMATS.join(', ')} 格式`
    };
  }
  
  return { isValid: true };
}

/**
 * 检查图片尺寸是否符合要求
 */
export async function validateImageDimensions(imageUrl: string): Promise<{
  isValid: boolean;
  reason?: string;
  width?: number;
  height?: number;
}> {
  try {
    // 处理可能存在的阿里云 OSS 参数（如 x-oss-process），确保获取的是原图信息
    let targetUrl = imageUrl;
    try {
      const urlObj = new URL(imageUrl);
      if (urlObj.searchParams.has('x-oss-process')) {
        urlObj.searchParams.delete('x-oss-process');
        targetUrl = urlObj.toString();
      }
    } catch (e) {
      // ignore invalid url
    }

    const imageInfo = await getImageInfo(targetUrl);
    const { width, height, size } = imageInfo;

    // 检查体积（最大 100MB）
    const MAX_IMAGE_SIZE = 104857600; // 100 * 1024 * 1024
    if (size && size > MAX_IMAGE_SIZE) {
      return {
        isValid: false,
        reason: `图片体积过大: ${(size / (1024 * 1024)).toFixed(2)}MB，最大支持 100MB`,
        width,
        height
      };
    }
    
    // 检查最小尺寸
    if (width < 80 || height < 80) {
      return {
        isValid: false,
        reason: `图片尺寸过小: ${width}x${height}，最小要求 80x80`,
        width,
        height
      };
    }
    
    // 检查最大尺寸
    if (width > 10000 || height > 10000) {
      return {
        isValid: false,
        reason: `图片尺寸过大: ${width}x${height}，最大支持 10000x10000`,
        width,
        height
      };
    }
    
    // 检查长宽比
    const ratio = Math.max(width, height) / Math.min(width, height);
    if (ratio > 2) {
      return {
        isValid: false,
        reason: `图片长宽比过大: ${ratio.toFixed(2)}:1，最大支持 2:1`,
        width,
        height
      };
    }
    
    // 检查分辨率（非严格参数）
    if (width * height < 40000) { // 200x200
      return {
        isValid: false,
        reason: `图片分辨率过低: ${width}x${height}，建议至少 200x200`,
        width,
        height
      };
    }
    
    return { isValid: true, width, height };
  } catch (error) {
    return {
      isValid: false,
      reason: '无法获取图片信息进行尺寸检查'
    };
  }
}

/**
 * 是否需要添加明水印
 * v2.3 变更：区分团队版和个人版的明水印逻辑
 * 
 * 逻辑：
 * - 团队版（未过期）：按 realizeResult 判断
 *   - rr === 5: 无水印
 *   - rr < 5: type=2（团队会员未签署）
 * - 团队版（已过期）：type=1
 * - 个人版（会员）：按 realizeResult 判断
 *   - rr === 5: 无水印
 *   - rr < 5: type=2（会员未签署）
 * - 个人版（非会员）：type=1
 */
export async function checkNeedWatermark() {
  let result = {
    needWatermark: true,
    userId: '',
    watermarkType: 1
  }

  try {
    const userStore = useUserStore();
    const userId = String(userStore.userInfo.uniqueId);
    result.userId = userId;

    // ========== 团队版判断 ==========
    if (userStore.isGroupLogin) {
      const groupStore = useGroupStore();
      
      // 团队版已过期 → type=1
      if (groupStore.isExpired) {
        result.needWatermark = true;
        result.watermarkType = 1;
        return result;
      }
      
      // 团队版未过期 → 按 realizeResult 判断
      const rr = userStore.userInfo.realizeResult ?? WatermarkStatusEnum.WAITING;
      
      if (rr === WatermarkStatusEnum.SIGNED_WATERMARK_OFF) {
        // 状态5: 已签署且关闭水印 → 无水印
        result.needWatermark = false;
      } else {
        // 状态0-4: 未签署/未关闭 → type=2（团队会员未签署）
        result.needWatermark = true;
        result.watermarkType = 2;
      }
      return result;
    }

    // ========== 个人版判断 ==========
    // 非会员 → type=1
    if (!userStore.isMemberUser) {
      result.needWatermark = true;
      result.watermarkType = 1;
      return result;
    }
    
    // 会员 → 按 realizeResult 判断
    const rr = userStore.userInfo.realizeResult ?? WatermarkStatusEnum.WAITING;
    
    if (rr === WatermarkStatusEnum.SIGNED_WATERMARK_OFF) {
      // 状态5: 已签署且关闭水印 → 无水印
      result.needWatermark = false;
    } else {
      // 状态0-4: 未签署/未关闭 → type=2（会员未签署）
      result.needWatermark = true;
      result.watermarkType = 2;
    }
    
  } catch (error) {
    const userStore = useUserStore();
    result.userId = String(userStore.userInfo.uniqueId);
    result.needWatermark = true;
    result.watermarkType = 1;
  }

  return result;
}

/**
 * 添加盲水印（仅使用阿里云，不支持时返回原图）
 */
async function addBlindWatermarkToImage(imageUrl: string): Promise<{
  url: string;
  method: 'aliyun' | 'none';
  hasBlindWatermark: boolean;
}> {
  // 首先尝试阿里云盲水印
  const formatValidation = validateImageForAliyunBlindWatermark(imageUrl);
  
  if (formatValidation.isValid) {
    try {
      // 使用阿里云服务
      const result = await addBlindWatermark({
        sourceUrl: imageUrl
      });
      
      // request 拦截器已解包 response.data.data，实际类型为 { targetUrl: string } | undefined
      const data = result as any as { targetUrl: string } | undefined;
      if (data?.targetUrl) {
        console.log('阿里云盲水印添加成功');
        return {
          url: data.targetUrl,
          method: 'aliyun',
          hasBlindWatermark: true
        };
      }
      
      console.warn('阿里云盲水印返回数据异常，使用原图');
    } catch (error) {
      console.error('阿里云盲水印服务失败:', error);
    }
  } else {
    console.warn('图片格式不符合阿里云要求:', formatValidation.reason);
  }
  
  // 阿里云不支持，直接返回原图
  console.log('阿里云盲水印不支持，使用原图');
  return {
    url: imageUrl,
    method: 'none',
    hasBlindWatermark: false
  };
}

/**
 * 下载图片为 Blob 对象
 * @param imageUrl 处理后的图片 URL
 * @param originalUrl 原始图片 URL（用于获取文件扩展名）
 * @param format 目标格式（可选，用于覆盖扩展名）
 * @returns 下载的 Blob 对象
 */
async function downloadImageAsBlob(
  imageUrl: string,
  originalUrl: string,
  format?: 'jpg' | 'png'
): Promise<Blob> {
  // 确定文件扩展名：如果指定了格式则使用格式，否则使用原图扩展名
  let ext = getFileExtension(originalUrl);
  if (format) {
    ext = format;
  }

  const fileName = `${nanoid()}.${ext}`;
  const downloadUrl = generateDownloadUrl(imageUrl, fileName);

  const downloader = new FileDownloader(downloadUrl, {
    chunkSize: 5 * 1024 * 1024,
    maxConcurrency: 4,
    maxRetries: 5,
    retryDelay: 2000,
    progress: () => {}
  });

  return await downloader.start();
}

/**
 * 生成带水印的 Blob 文件（包含明水印和盲水印）
 * @param imageUrl 图片地址
 * @param options 配置项
 * @param options.format 目标图片格式 (jpg/png),
 * @param options.skipBlindWatermark 跳过盲水印（仅去阿里云盲水印，明水印逻辑不受影响）
 */
export async function generateWatermarkedBlob(imageUrl: string, options?: { format?: 'jpg' | 'png'; skipBlindWatermark?: boolean }): Promise<{
  blob: Blob;
  hasBlindWatermark: boolean;
  userId: string;
}> {
  const { needWatermark, userId, watermarkType } = await checkNeedWatermark();

  // 检查图片格式是否支持阿里云盲水印
  const formatValidation = validateImageForAliyunBlindWatermark(imageUrl);
  let shouldTryBlindWatermark = false;

  if (formatValidation.isValid) {
    // 检查图片尺寸是否符合阿里云要求
    try {
      // 这里的 validateImageDimensions 已经支持自动剔除 oss 参数查原图
      const dimensionValidation = await validateImageDimensions(imageUrl);
      if (dimensionValidation.isValid) {
        shouldTryBlindWatermark = true;
      } else {
        console.warn('图片尺寸不符合阿里云要求:', dimensionValidation.reason);
      }
    } catch (error) {
      console.warn('无法获取图片信息进行尺寸检查:', error);
    }
  } else {
    console.warn('图片格式不符合阿里云要求:', formatValidation.reason);
  }

  // 根据检查结果决定是否添加盲水印
  // skipBlindWatermark 仅关闭盲水印步骤，不影响下方明水印逻辑（明水印仍受 shouldTryBlindWatermark 约束）
  let blindWatermarkResult;
  const applyBlindWatermark = shouldTryBlindWatermark && !options?.skipBlindWatermark;
  if (applyBlindWatermark) {
    blindWatermarkResult = await addBlindWatermarkToImage(imageUrl);
  } else {
    // 直接使用原图，不添加盲水印
    blindWatermarkResult = {
      url: imageUrl,
      method: 'none' as const,
      hasBlindWatermark: false
    };
  }

  // 再处理明水印
  let finalUrl = blindWatermarkResult.url;
  let canApplyVisibleWatermark = true;
  if (needWatermark && shouldTryBlindWatermark) {
    try {
      const dimensionValidationAgain = await validateImageDimensions(blindWatermarkResult.url);
      if (!dimensionValidationAgain.isValid) {
        canApplyVisibleWatermark = false;
      }
    } catch (error) {
      canApplyVisibleWatermark = false;
    }

    if (canApplyVisibleWatermark) {
      finalUrl = generateOriginalWatermarkedImage(blindWatermarkResult.url, {
        watermarkType: watermarkType
      });
    }
  }

  // 处理格式转换 (在水印处理之后进行)
  if (options?.format) {
    try {
      const urlObj = new URL(finalUrl);
      const params = urlObj.searchParams;
      const targetFormat = options.format === 'jpg' ? 'jpg' : 'png';
      const formatParam = `image/format,${targetFormat}`;

      if (params.has('x-oss-process')) {
        // 如果已有处理参数，使用管道符 | 追加格式转换
        const currentProcess = params.get('x-oss-process');
        // 避免重复添加
        if (!currentProcess?.includes(formatParam)) {
          params.set('x-oss-process', `${currentProcess}|${formatParam}`);
        }
      } else {
        params.set('x-oss-process', formatParam);
      }

      // 重新构造 URL
      // 阿里云 OSS 参数中的逗号不需要编码，但 URLSearchParams 会编码成 %2C
      finalUrl = urlObj.toString().replace(/%2C/g, ',');
    } catch (e) {
      console.warn('添加格式转换参数失败:', e);
    }
  }

  // 使用抽离的下载函数获取最终 Blob
  const finalBlob = await downloadImageAsBlob(finalUrl, imageUrl, options?.format);

  return {
    blob: finalBlob,
    hasBlindWatermark: blindWatermarkResult.hasBlindWatermark,
    userId
  };
}

/**
 * 下载远程图片并添加水印
 * @param imageUrl 图片地址
 * @param options.format 目标下载格式（jpg/png），传入后通过 OSS 实时转码，大幅减小下载体积
 * @param options.skipBlindWatermark 跳过盲水印（保留明水印）
 */
export async function downloadWatermarkedImage(imageUrl: string, options?: { format?: 'jpg' | 'png'; skipBlindWatermark?: boolean }) {
  // 根据 format 修正文件名后缀，确保下载文件与实际格式一致
  const ext = options?.format || getFileExtension(imageUrl);
  const fileName = `${nanoid()}.${ext}`;

  try {
    // 直接使用 generateWatermarkedBlob 处理所有水印逻辑，透传格式与盲水印开关
    // 直接使用 generateWatermarkedBlob 处理所有水印逻辑，传入格式参数触发 OSS 实时转码
    const { blob: watermarkedBlob } = await generateWatermarkedBlob(imageUrl, options);
    
    // 触发下载
    const url = URL.createObjectURL(watermarkedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // 延迟释放 URL，确保浏览器有足够时间开始下载
    // 浏览器下载是异步行为，立即 revoke 可能导致大文件下载失败
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 3000); // 3秒后释放，给用户足够的下载时间
  } catch (error) {
    logger.error('水印图片下载失败', { error, imageUrl });
    // 降级处理：直接打开原图链接
    const { needWatermark, watermarkType } = await checkNeedWatermark();
    let fallbackUrl = imageUrl;
    
    if (needWatermark) {
      fallbackUrl = generateOriginalWatermarkedImage(imageUrl, {
        watermarkType: watermarkType
      });
    }

    fallbackUrl = generateDownloadUrl(fallbackUrl, fileName);
    window.open(fallbackUrl);

    // 上面都错误的兜底URL
    const sourceUrl = fallbackUrl.split('?')[0]

    notification.error({
      placement: "topRight",
      duration: 0,
      message: "下载失败？",
      description: h("p", {}, [
        "请确认您的网络是否正常，正常后再",
        h(
          "a",
          {
            style: {
              color: "#1890ff",
              textDecoration: "underline",
              cursor: "pointer"
            },
            target: "_blank",
            href: sourceUrl,
            download: fileName
          },
          "点击手动下载保存"
        )
      ])
    });
    throw error;
  }
}

/**
 * 批量下载图片并添加水印，打包成zip文件
 * @param options.skipBlindWatermark 跳过盲水印（保留明水印）
 */
export async function downloadWatermarkedImagesAsZip(
  imageUrls: string[],
  zipFileName?: string,
  onProgress?: (current: number, total: number) => void,
  options?: { skipBlindWatermark?: boolean }
) {
  if (!imageUrls || imageUrls.length === 0) {
    throw new Error("图片URL数组不能为空");
  }

  // 动态导入 JSZip
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const finalZipFileName = zipFileName || `images_${nanoid()}.zip`;
  
  try {
    // 批量处理图片
    for (let i = 0; i < imageUrls.length; i++) {
      const imageUrl = imageUrls[i];
      onProgress?.(i + 1, imageUrls.length);
      
      try {
        const { blob } = await generateWatermarkedBlob(imageUrl, options);
        const fileExtension = getFileExtension(imageUrl);
        const fileName = `image_${i + 1}_${nanoid()}.${fileExtension}`;
        
        // 添加到zip文件中
        zip.file(fileName, blob);
      } catch (error) {
        console.warn(`处理图片 ${imageUrl} 失败:`, error);
        // 继续处理其他图片，不中断整个流程
      }
    }
    
    // 生成zip文件
    const zipBlob = await zip.generateAsync({ type: "blob" });
    
    // 触发下载
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = finalZipFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    URL.revokeObjectURL(url);
    
    notification.success({
      placement: "topRight",
      message: "批量下载完成",
      description: `成功打包 ${imageUrls.length} 张图片为 ${finalZipFileName}`
    });
    
  } catch (error) {
    logger.error('批量下载水印图片失败', { error, imageUrls });
    notification.error({
      placement: "topRight",
      message: "批量下载失败",
      description: "打包过程中发生错误，请重试"
    });
    throw error;
  }
}
